/**
 * `/zu-zahlen` — Henris Liste.
 *
 * Ablauf 4: Beleg, Betrag, fällig am. Henri überweist im Online-Banking, setzt
 * den Haken, das System trägt das Datum ein. Sonst nichts.
 *
 * Zwei Dinge sind an dieser Seite nicht verhandelbar:
 *
 * 1. **Gemahntes steht ganz oben, rot.** Eine Mahnung, die man erst beim
 *    Scrollen findet, ist keine Mahnung (Ablauf 4a).
 * 2. **Nur bestätigte IBANs werden angezeigt.** Der Server schickt in `iban`
 *    ausschliesslich die aus `bh_supplier_ibans`, und nur wenn sie per
 *    Rueckruf bestaetigt ist (§15.1 S3). Steht dort `null`, zeigt die Liste
 *    „IBAN unbestätigt" — und **nicht** die vom Beleg. Wer sie hier
 *    hinschriebe, haette den ganzen Aufwand der Echtheitspruefung umsonst
 *    betrieben: genau die geaenderte IBAN ist der haeufigste Betrug.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertOctagon, Check, ShieldQuestion } from 'lucide-react';
import { clsx } from 'clsx';

import { bh, fehlerText } from '@/lib/api';
import { euro, faelligText, heuteIso, iban as ibanFormat } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { AmpelChip, Chip } from '@/components/ui/Chip';
import { Dialog } from '@/components/ui/Dialog';
import { Eingabe, Feld, Seitenkopf } from '@/components/ui/Feld';
import { useToast } from '@/components/ui/Toast';
import { Fehler, Laden, Leer } from '@/components/ui/Zustaende';
import type { ZuZahlenEintrag } from '@/types';

export function ZuZahlenPage() {
  const cache = useQueryClient();
  const toast = useToast();
  const [nurFaellig, setNurFaellig] = useState(false);
  const [bezahlt, setBezahlt] = useState<ZuZahlenEintrag | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['zu-zahlen', nurFaellig],
    queryFn: () => bh.zuZahlen(nurFaellig),
  });

  const abhaken = useMutation({
    mutationFn: ({ id, datum, betrag }: { id: string; datum: string; betrag?: string }) =>
      bh.belegBezahlt(id, { datum, betrag: betrag || null }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['zu-zahlen'] });
      void cache.invalidateQueries({ queryKey: ['belege'] });
      setBezahlt(null);
      toast.erfolg('Als bezahlt eingetragen.');
    },
    onError: (fehler) => toast.fehler(fehlerText(fehler)),
  });

  return (
    <div>
      <Seitenkopf
        titel="Zu zahlen"
        beschreibung="Offene Rechnungen. Gemahntes steht oben — drei Tage vor Fälligkeit erinnert die Liste, nicht danach."
      >
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={nurFaellig}
            onChange={(e) => setNurFaellig(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
          />
          nur bald fällige
        </label>
      </Seitenkopf>

      {data && data.items.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-6 rounded-xl border border-slate-200 bg-white px-5 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Offen</p>
            <p className="text-lg font-semibold tabular-nums text-slate-900">
              {euro(data.summe_offen)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Posten</p>
            <p className="text-lg font-semibold tabular-nums text-slate-900">{data.items.length}</p>
          </div>
          {data.anzahl_gemahnt > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-danger-600">Gemahnt</p>
              <p className="text-lg font-semibold tabular-nums text-danger-700">
                {data.anzahl_gemahnt}
              </p>
            </div>
          )}
        </div>
      )}

      {isLoading && <Laden />}
      {isError && <Fehler text={fehlerText(error)} onErneut={() => void refetch()} />}

      {data && data.items.length === 0 && (
        <Leer
          titel="Nichts offen."
          hinweis="Alles bezahlt oder noch nichts freigegeben. Auf die Liste kommt ein Beleg erst, wenn er die Echtheitsprüfung bestanden hat oder freigegeben wurde."
        />
      )}

      <div className="space-y-2">
        {data?.items.map((eintrag) => (
          <Zeile key={eintrag.id} eintrag={eintrag} onAbhaken={() => setBezahlt(eintrag)} />
        ))}
      </div>

      {bezahlt && (
        <BezahltDialog
          eintrag={bezahlt}
          laeuft={abhaken.isPending}
          onAbbrechen={() => setBezahlt(null)}
          onBestaetigen={(datum, betrag) => abhaken.mutate({ id: bezahlt.id, datum, betrag })}
        />
      )}
    </div>
  );
}

function Zeile({ eintrag, onAbhaken }: { eintrag: ZuZahlenEintrag; onAbhaken: () => void }) {
  const ueberfaellig = (eintrag.tage_bis_faellig ?? 99) < 0;

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-4 rounded-xl border bg-white px-5 py-4',
        eintrag.gemahnt ? 'border-danger-200 bg-danger-50/40' : 'border-slate-200',
      )}
    >
      <div className="min-w-[14rem] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/eingang/${eintrag.id}`}
            className="font-medium text-slate-900 hover:text-primary-700"
          >
            {/* Bei einer Auslage wird nicht der Lieferant bezahlt, sondern der
                Mensch, der ausgelegt hat — die Liste sagt das direkt (§15.2). */}
            {eintrag.ist_auslage
              ? `Erstattung an ${eintrag.auslage_von ?? 'unbekannt'}`
              : (eintrag.supplier_name ?? 'unbekannter Aussteller')}
          </Link>
          {eintrag.gemahnt && (
            <Chip klassen="border-danger-200 bg-danger-100 text-danger-700">
              <AlertOctagon size={12} />
              gemahnt
            </Chip>
          )}
          <AmpelChip rot={eintrag.warnungen_rot} gelb={eintrag.warnungen_gelb} />
        </div>
        <p className="mt-0.5 text-xs text-slate-500">
          {eintrag.invoice_number ?? 'ohne Nummer'}
        </p>
      </div>

      <div className="w-40">
        <p className="text-xs uppercase tracking-wide text-slate-400">Zahlung an</p>
        {eintrag.iban_stand === 'bestaetigt' && eintrag.iban ? (
          <p className="font-mono text-xs text-slate-700">{ibanFormat(eintrag.iban)}</p>
        ) : (
          <p
            className="inline-flex items-center gap-1 text-xs font-medium text-warning-700"
            title="Es gibt eine IBAN auf dem Beleg — aber niemand hat sie per Rückruf bestätigt. Zum Abtippen zeigt die Liste sie deshalb nicht."
          >
            <ShieldQuestion size={12} />
            {eintrag.iban_stand === 'keine' ? 'keine IBAN hinterlegt' : 'IBAN unbestätigt'}
          </p>
        )}
      </div>

      <div className="w-36 text-right">
        <p className="text-xs uppercase tracking-wide text-slate-400">Betrag</p>
        <p className="tabular-nums font-medium text-slate-900">{euro(eintrag.amount_gross)}</p>
      </div>

      <div className="w-40">
        <p className="text-xs uppercase tracking-wide text-slate-400">Fällig</p>
        <p
          className={clsx(
            'text-sm',
            ueberfaellig ? 'font-medium text-danger-700' : 'text-slate-700',
          )}
        >
          {faelligText(eintrag.tage_bis_faellig)}
        </p>
      </div>

      <Button variant="outline" size="sm" onClick={onAbhaken}>
        <Check size={14} className="mr-1.5" />
        Bezahlt
      </Button>
    </div>
  );
}

/**
 * Der Haken mit Datumsdialog.
 *
 * Ein `window.confirm` kann kein Datum abfragen — und das Datum ist der Punkt:
 * ueberwiesen wird oft nicht am selben Tag, an dem man den Haken setzt, und
 * der Bankabgleich sucht spaeter danach.
 */
function BezahltDialog({
  eintrag,
  laeuft,
  onAbbrechen,
  onBestaetigen,
}: {
  eintrag: ZuZahlenEintrag;
  laeuft: boolean;
  onAbbrechen: () => void;
  onBestaetigen: (datum: string, betrag?: string) => void;
}) {
  const [tag, setTag] = useState(heuteIso());
  const [abweichend, setAbweichend] = useState('');

  return (
    <Dialog
      offen
      onSchliessen={onAbbrechen}
      titel="Als bezahlt eintragen"
      hinweis={`${
        eintrag.ist_auslage
          ? `Erstattung an ${eintrag.auslage_von ?? 'unbekannt'}`
          : (eintrag.supplier_name ?? 'unbekannter Aussteller')
      } · ${euro(eintrag.amount_gross)}`}
      abbrechenBeiHintergrundklick={false}
      fussleiste={
        <>
          <Button variant="outline" onClick={onAbbrechen} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button
            onClick={() => onBestaetigen(tag, abweichend.trim() || undefined)}
            disabled={!tag}
            loading={laeuft}
          >
            Eintragen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Feld label="Überwiesen am" pflicht>
          <Eingabe type="date" value={tag} onChange={(e) => setTag(e.target.value)} />
        </Feld>
        <Feld
          label="Abweichender Betrag"
          hinweis="Nur ausfüllen, wenn etwas anderes überwiesen wurde als der Rechnungsbetrag."
        >
          <Eingabe
            value={abweichend}
            onChange={(e) => setAbweichend(e.target.value)}
            placeholder={eintrag.amount_gross ?? ''}
          />
        </Feld>
      </div>
    </Dialog>
  );
}
